import React, { useState, useEffect } from "react";
import { useThunk } from "../../hooks/useThunk";
import { useSelector } from "react-redux";
import { getAllsupportEmails, updateSupportEmails } from "../../store";
import Input from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButton";
import showToast from "../../utils/toastNotifications"; 

const SupportEmailSettings = () => {
  const [legalSupportEmail, setLegalSupportEmail] = useState("");
  const [legalSupportEmailError, setLegalSupportEmailError] = useState("");
  const [reqMaintenanceEmail, setReqMaintenanceEmail] = useState("");
  const [reqMaintenanceEmailError, setReqMaintenanceEmailError] = useState("");
  const [taxConsultEmail, setTaxConsultEmail] = useState("");
  const [taxConsultEmailError, setTaxConsultEmailError] = useState("");

  const supportEmails = useSelector((state) => state.supportEmailConfig.supportEmails.emails);

  const [doGetSupportEmails, isGetting, supportError] = useThunk(getAllsupportEmails);
  const [doUpdateSupportEmails, isUpdating, updateError] = useThunk(updateSupportEmails);

  const MAX_EMAIL_LENGTH = 1000;

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const fetchSupportEmails = async () => {
    const result = await doGetSupportEmails();
    if(result.success) {
      console.log("Support emails loaded successfully");
    } else {
      showToast("error", "Failed to load support emails");
    }
  }

  useEffect(() => {
    fetchSupportEmails();
  }, []);

  const loadSupportEmails = () => {
    setLegalSupportEmail((supportEmails?.legalSupportEmail || []).join(", "));
    setReqMaintenanceEmail((supportEmails?.reqMaintenanceEmail || []).join(", "));
    setTaxConsultEmail((supportEmails?.taxConsultEmail || []).join(", "));
  };

  useEffect(() => {
    loadSupportEmails();
  }, [supportEmails]);

  const handleCancel = () => {
    loadSupportEmails();
    setLegalSupportEmailError("");
    setReqMaintenanceEmailError("");
    setTaxConsultEmailError("");
  }

  const hasChanges = () => {
    return (
      legalSupportEmail !== (supportEmails?.legalSupportEmail || []).join(", ") ||
      reqMaintenanceEmail !== (supportEmails?.reqMaintenanceEmail || []).join(", ") ||
      taxConsultEmail !== (supportEmails?.taxConsultEmail || []).join(", ")
    );
  }

  const parseAndValidateEmails = (emailsString) => {
    // Normalize: replace semicolons, newlines, and multiple spaces with commas
    const normalized = emailsString
      .replace(/[\s;\n\r]+/g, ",")
      .replace(/,+/g, ",")
      .replace(/^,|,$/g, "");

    const emailsArray = normalized
      .split(",")
      .map((em) => em.trim())
      .filter(Boolean);

    let isValid = true;
    for (const email of emailsArray) {
      if (!isValidEmail(email)) {
        isValid = false;
        break;
      }
    }

    return {
      emailsArray,
      isValid
    };
  };

  const handleSave = async () => {

    const {emailsArray: legalSupportEmailArray, isValid: isValidLegal} = parseAndValidateEmails(legalSupportEmail);
    const {emailsArray: reqMaintenanceEmailArray, isValid: isValidReq} = parseAndValidateEmails(reqMaintenanceEmail);
    const {emailsArray: taxConsultEmailArray, isValid: isValidTax} = parseAndValidateEmails(taxConsultEmail);

    setLegalSupportEmailError(isValidLegal ? "" : "Invalid email");
    setReqMaintenanceEmailError(isValidReq ? "" : "Invalid email");
    setTaxConsultEmailError(isValidTax ? "" : "Invalid email");

    // If any are invalid, stop execution
    if (!isValidLegal || !isValidReq || !isValidTax) {
      return;
    }

    const data = {
      legalSupportEmail: legalSupportEmailArray.length > 0 ? legalSupportEmailArray : null,
      reqMaintenanceEmail: reqMaintenanceEmailArray.length > 0 ? reqMaintenanceEmailArray : null,
      taxConsultEmail: taxConsultEmailArray.length > 0 ? taxConsultEmailArray : null
    };

    const result = await doUpdateSupportEmails(data);
    if (result.success) {
      showToast("success", "Support emails updated successfully");
    } else {
      showToast("error", result.error.message || "Failed to update support emails");
    }
  }

  const handleOnKeyDown = (value, setValue, setError) => (e) => {
    if (e.key === " " || e.key === "Enter") {
      let emails = value.split(",").map((em) => em.trim()).filter(Boolean);
      const lastEmail = emails[emails.length - 1];

      if (lastEmail && isValidEmail(lastEmail)) {
        // Add comma if not already present
        if (!value.trim().endsWith(",")) {
          setValue(value.trim() + ", ");
          return;
        }
      } else if (lastEmail) {
        setError("Invalid email");
        e.preventDefault();
      }
    }
  }

  const checkEmailLength = (e) => {
    const value = e.target.value;
    if (value.length >= MAX_EMAIL_LENGTH) {
      return "Maximum character limit reached.";
    }
  }

  const handleOnchange = (setValue, setError) => (e) => {
      const error = checkEmailLength(e);
        if (error) {
          setError(error);
          return;
        }
        setValue(e.target.value);
        setError("");
  };

  return (
    <div>
      <div className="mx-[30px] mt-[30px] w-auto rounded-[10px] bg-white h-full p-[30px] space-y-[10px] flex flex-col">
        <div className="h-[260px]">
          <h1 className="font-inter font-semibold text-[22px] text-blue mb-[30px]">
            Legal Support
          </h1>
          <div className="h-[22px] mb-[5px] ">
            <h1 className="font-inter text-[17px] fontt-medium text-[#09114B] ">
              Legal Support Emails
            </h1>
          </div>
          <Input
            id="legalSupportEmail"
            name="legalSupportEmail"
            label=""
            placeholder="Enter Legal Support Emails..."
            type="textarea"
            value={legalSupportEmail}
            onChange={handleOnchange(setLegalSupportEmail, setLegalSupportEmailError)}
            onKeyDown={handleOnKeyDown(legalSupportEmail, setLegalSupportEmail, setLegalSupportEmailError)}
            inputStyle="text-sm"
            className="rounded-[5px] h-[135px] text-[15px]"
          />
          {legalSupportEmailError && (
            <div className="text-red-600 text-xs mt-1">{legalSupportEmailError}</div>
          )}
        </div>

        <div className="h-[260px]">
          <h1 className="font-inter font-semibold text-[22px] text-blue mb-[30px]">
            Maintenance Request{" "}
          </h1>
          <div className="h-[22px] mb-[5px] ">
            <h1 className="font-inter text-[17px] fontt-medium text-[#09114B] ">
              Maintenance Emails{" "}
            </h1>
          </div>
          <Input
            id="reqMaintenanceEmail"
            name="reqMaintenanceEmail "
            label=""
            placeholder="Enter Legal Maintenance Emails..."
            type="textarea"
            value={reqMaintenanceEmail}
            onChange={handleOnchange(setReqMaintenanceEmail, setReqMaintenanceEmailError)}
            onKeyDown={handleOnKeyDown(reqMaintenanceEmail, setReqMaintenanceEmail, setReqMaintenanceEmailError)}
            inputStyle="text-sm"
            className="rounded-[5px] h-[135px] text-[15px]"
          />
          {reqMaintenanceEmailError && (
            <div className="text-red-600 text-xs mt-1">{reqMaintenanceEmailError}</div>
          )}
        </div>

        <div className="h-[260px]">
          <h1 className="font-inter font-semibold text-[22px] text-blue mb-[30px]">
            Tax Consultation{" "}
          </h1>
          <div className="h-[22px] mb-[5px] ">
            <h1 className="font-inter text-[17px] fontt-medium text-[#09114B] ">
              Tax Support Emails{" "}
            </h1>
          </div>
          <Input
            id="taxConsultEmail"
            name="taxConsultEmail"
            label=""
            placeholder="Enter Tax Support Emails..."
            type="textarea"
            value={taxConsultEmail}
            onChange={handleOnchange(setTaxConsultEmail, setTaxConsultEmailError)}
            onKeyDown={handleOnKeyDown(taxConsultEmail, setTaxConsultEmail, setTaxConsultEmailError)}
            inputStyle="text-sm"
            className="rounded-[5px] h-[135px] text-[15px]"
          />
          {taxConsultEmailError && (
            <div className="text-red-600 text-xs mt-1">{taxConsultEmailError}</div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row w-full items-center justify-center space-y-[19px] sm:space-y-[0px] sm:space-x-[19px] ">
          <CustomButton
            type="Cancel"
            className="h-[45px] sm:h-[55px] bg-white text-[#93ABF3] border-2 w-full sm:w-[131px] border-[#93ABF3] font-jost font-semibold text-[18px] leading-[28px] rounded-[8px]"
            buttonText="Cancel"
            onClick={() => {handleCancel()}}
          />
          <CustomButton
            type="Save"
            className="h-[45px] sm:h-[55px] bg-[#4772F1] text-white border-2 w-full sm:w-[170px] border-[#4772F1] font-jost font-semibold text-[18px] leading-[28px] rounded-[8px]"
            buttonText="Save"
            disabled={!hasChanges()}
            onClick={() => {handleSave()}}
            loading={isUpdating}
          />
        </div>
      </div>
    </div>
  );
};

export default SupportEmailSettings;
